import {
  logout,
  getInfo,
  getTenantList,
  changeCurrentTenant,
  getIAMLoginUrl,
  validateToken,
  isAdmin,
  // getLoginUrl,
  requestIHaierUserInfo,
  refreshTokenApi
} from '@/api/user'
import { requestApps } from '@/api/app'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { appWithCanAccess } from '@/api/app.js'
import {
  getPublicKeyApi,
  newNewRegistAgentSystemApi,
  fetchAndCacheWorkspaceId,
  restoreWorkspaceId,
  getAgentListApi
} from '@/api/agentAuth'
import { buildAgentIdMap } from './cosmoClaw'
import store from '..'
import Cookies from 'js-cookie'
import JSEncrypt from 'jsencrypt'
// Session-level tianzhi agent token cache: avoid re-registering on every page
// enter, which rotates the token and kicks the established shared WS connection.
let _lastAgentToken = ''
// In-flight token fetch (dedupe concurrent initializations)
let _agentTokenPromise = null
// 智能体列表本地缓存 key（接口失败时兜底渲染，避免页面卡在加载态）
const AGENT_LIST_CACHE_KEY = 'claw_agent_list'

const getDefaultState = () => {
  return {
    // token信息
    token: '',
    // 名称
    name: '',
    // 头像
    avatar: '',
    isAdmin: false,
    // 用户信息
    userInfo: null,
    // 权限菜单
    appList: [],
    // 角色
    roles: [],
    // 系统风格
    skin: '',
    // 系统配置
    sysConfig: {},
    watermark: false,
    tenantList: [],
    currentTenant: {},
    agentList: [],
    // 智能体列表是否已加载完成（成功或回退缓存，用于默认入口/无目标跳转判断）
    agentListLoaded: false
  }
}
// 获取默认状态
const state = getDefaultState()
const mutations = {
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState())
  },
  SET_TOKEN: (state, token) => {
    state.token = token
  },
  SET_WATERMARK: (state, watermark) => {
    state.watermark = watermark
  },
  SET_NAME: (state, name) => {
    state.name = name
  },
  SET_ISADMIN: (state, isAdmin) => {
    state.isAdmin = isAdmin
  },
  SET_AVATAR: (state, avatar) => {
    state.avatar = avatar
  },
  // 设置用户信息
  SET_USERINFO: (state, userInfo) => {
    state.userInfo = userInfo
    localStorage.setItem('eiipUserInfo', JSON.stringify(userInfo))
    Cookies.set(
      'eiipUserInfo',
      JSON.stringify({
        userCode: userInfo.userCode,
        userName: userInfo.userName
      }),
      { expires: 1 }
    )
  },
  SET_ENTERPRISE_INFO: (state, enterpriseInfo) => {
    state.userInfo = {
      ...(state.userInfo || {}),
      enterpriseInfoSubmitted: true,
      enterpriseName: enterpriseInfo.enterpriseName,
      socialCreditCode: enterpriseInfo.socialCreditCode
    }
    localStorage.setItem('eiipUserInfo', JSON.stringify(state.userInfo))
  },
  SET_TENANT_LIST: (state, tenantList) => {
    state.tenantList = tenantList
  },
  SET_CURRENT_TENANT: (state, currentTenant) => {
    state.currentTenant = currentTenant
  },
  SET_AGENT_LIST: (state, agentList) => {
    state.agentList = agentList
  },
  SET_AGENT_LIST_LOADED: (state, val) => {
    state.agentListLoaded = !!val
  },
  // 设置权限菜单
  SET_APP_LIST: (state, appList) => {
    state.appList = appList
  },
  SET_ALL_PERMISSIONS: (state, allPermissions) => {
    state.allPermissions = allPermissions
  },
  SET_SKIN: (state, skin) => {
    state.skin = skin
  },
  SET_CONFIG: (state, config) => {
    state.sysConfig = config
    Cookies.set('sysConfig', JSON.stringify(config), { expires: 1 })
  }
}

const actions = {
  /**
   * iam登录
   * @param {*} param0
   * @param {*} token 对应的token字符串
   * @returns
   */
  iamLogin({ commit, dispatch }, token) {
    return new Promise((resolve, reject) => {
      try {
        commit('SET_TOKEN', token)
        setToken(token)
        // 校验token有效性
        dispatch('validateToken', token)
        // 请求用户信息
        dispatch('getInfo')
        dispatch('getTenantList')
        // 获取并存储 tianzhi agent token（登录后自动换取智能体平台凭证）
        // Fetch the agent platform token on demand from smart workbench.
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  },

  /**
   * 获取并存储 tianzhi agent token
   * 调用智能体平台的公钥接口和注册接口，换取 agent token 后存入 cookie
   * @returns {Promise<void>}
   */
  async fetchAgentToken() {
    const token = getToken()
    if (!token) {
      console.warn('[fetchAgentToken] 未获取到门户 token，跳过')
      return
    }

    // token 存储 key 从环境变量读取，由 .env 文件按环境配置
    const agentTokenKey = process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
    const currentCookie = Cookies.get(agentTokenKey)

    // Session-level cache: reuse the token fetched this session as long as the
    // cookie still holds it, instead of re-registering (which rotates the token).
    if (_lastAgentToken && currentCookie === _lastAgentToken) {
      return _lastAgentToken
    }
    // 已有 token cookie 时直接复用，避免每次刷新页面都重新注册（public_key + new_c_regist 两次请求）。
    // token 过期时由 agentAuth 的 401 拦截器自动刷新重试，因此这里复用是安全的。
    if (currentCookie) {
      _lastAgentToken = currentCookie
      return currentCookie
    }
    // Dedupe concurrent fetches so parallel initializations cannot mint two tokens.
    if (_agentTokenPromise) return _agentTokenPromise

    _agentTokenPromise = (async () => {
      try {
      // 1. 获取 RSA 公钥
        const pubRes = await getPublicKeyApi()
        const publicKey = pubRes.data?.public_key || pubRes.public_key
        if (!publicKey) {
          console.warn('[fetchAgentToken] 获取公钥失败，响应:', pubRes)
          return
        }
        // 2. 使用公钥加密固定密码
        const encrypt = new JSEncrypt()
        encrypt.setPublicKey(publicKey)
        const password = encrypt.encrypt('Cosmoplat@2024')
        if (!password) {
          console.warn('[fetchAgentToken] 密码加密失败')
          return
        }
        // 3. 调用注册接口获取 agent token
        const regRes = await newNewRegistAgentSystemApi(password, token)
        const regData = regRes.data || regRes
        if (!regData || !regData.access_token) {
          console.warn('[fetchAgentToken] 注册接口未返回 access_token，响应:', regRes)
          return
        }
        // 4. 存储 agent token（仅存 cookie，统一存取入口）
        const accessToken = regData.access_token
        _lastAgentToken = accessToken
        Cookies.set(agentTokenKey, accessToken, {
          path: '/',
          SameSite: 'None',
          Secure: true
        })
        // 5. 存储 third_user_name
        if (regData.third_user_name) {
          localStorage.setItem('cosmo_claw_user_name', regData.third_user_name)
        }
        // 6. 存储 platform_role
        if (regData.platform_role) {
          sessionStorage.setItem('platform_role', regData.platform_role)
        }
        console.log('[fetchAgentToken] 成功获取并存储 agent token:', agentTokenKey)
        return accessToken
      } catch (err) {
        console.warn('[fetchAgentToken] 获取 agent token 失败:', err)
      // 不抛出错误，不阻塞主登录流程
      } finally {
        _agentTokenPromise = null
      }
    })()
    return _agentTokenPromise
  },
  /**
   * 顺序获取：agent token → 工作区信息 → 智能体/数字人列表
   * 每次 getInfo 后都应调用此 action
   */
  async fetchWorkspaceAndAgents({ commit, dispatch }) {
    console.log('[fetchWorkspaceAndAgents] 开始获取工作区和智能体信息')

    try {
      await dispatch('fetchAgentToken')
    } catch (err) {
      console.warn('[fetchWorkspaceAndAgents] fetchAgentToken 异常:', err)
    }

    // 每次进入页面/刷新都先从接口获取真实工作区 ID（不依赖 localStorage 缓存，
    // 避免刷新后一直使用写死/过期的工作区 ID）
    try {
      await fetchAndCacheWorkspaceId(true)
      console.log('[fetchWorkspaceAndAgents] 已从接口获取工作区信息')
    } catch (err) {
      console.warn('[fetchWorkspaceAndAgents] 获取工作区信息失败，回退本地缓存:', err)
      // 接口失败时从 localStorage 恢复上次接口获取的真实工作区 ID，避免后续请求不可用
      try {
        restoreWorkspaceId()
      } catch (e) {
        // ignore
      }
    }

    try {
      const res = await getAgentListApi()
      const data = res.data || res
      const agentList = data.list || data.records || data || []
      commit('SET_AGENT_LIST', agentList)
      buildAgentIdMap(agentList)
      commit('SET_AGENT_LIST_LOADED', true)
      try {
        localStorage.setItem(AGENT_LIST_CACHE_KEY, JSON.stringify(agentList))
      } catch (e) {
        // ignore: 存储失败不影响主流程
      }
      console.log('[fetchWorkspaceAndAgents] 智能体列表已缓存，共', Array.isArray(agentList) ? agentList.length : '?', '个')
    } catch (err) {
      console.warn('[fetchWorkspaceAndAgents] 获取智能体列表失败:', err)
      // 网络异常时回退到本地缓存，避免页面一直停留在加载态
      try {
        const cached = JSON.parse(localStorage.getItem(AGENT_LIST_CACHE_KEY) || 'null')
        if (Array.isArray(cached) && cached.length > 0) {
          commit('SET_AGENT_LIST', cached)
          buildAgentIdMap(cached)
          commit('SET_AGENT_LIST_LOADED', true)
          console.log('[fetchWorkspaceAndAgents] 使用本地缓存的智能体列表，共', cached.length, '个')
        }
      } catch (e) {
        // ignore: 缓存损坏时忽略
      }
    }
  },
  /**
   * 刷新智能体/数字人列表（订阅数字人成功后调用，同步左侧菜单树）
   * 仅重新拉取 agentList 并重建 id 映射，不重复获取 token / 工作区信息
   */
  async refreshAgentList({ commit }) {
    try {
      const res = await getAgentListApi()
      const data = res.data || res
      const agentList = data.list || data.records || data || []
      commit('SET_AGENT_LIST', agentList)
      buildAgentIdMap(agentList)
      commit('SET_AGENT_LIST_LOADED', true)
      try {
        localStorage.setItem(AGENT_LIST_CACHE_KEY, JSON.stringify(agentList))
      } catch (e) {
        // ignore: 存储失败不影响主流程
      }
      console.log('[refreshAgentList] 智能体列表已刷新，共', Array.isArray(agentList) ? agentList.length : '?', '个')
      return agentList
    } catch (err) {
      console.warn('[refreshAgentList] 刷新智能体列表失败:', err)
      // 网络异常时回退到本地缓存，避免左侧菜单树一直停留在旧状态
      try {
        const cached = JSON.parse(localStorage.getItem(AGENT_LIST_CACHE_KEY) || 'null')
        if (Array.isArray(cached) && cached.length > 0) {
          commit('SET_AGENT_LIST', cached)
          buildAgentIdMap(cached)
          commit('SET_AGENT_LIST_LOADED', true)
          return cached
        }
      } catch (e) {
        // ignore: 缓存损坏时忽略
      }
      throw err
    }
  },
  /**
   * 登录
   * @returns {void}
   */
  login() {
    return new Promise((resolve, reject) => {
      // window.localStorage.setItem('beforeUrl', window.location.href)
      // window.location.href = `${process.env.VUE_APP_BASE_API}base/user/authorizeLogin?pageUrl=${window.location.href}`
      // getLoginUrl(window.location.href)
      // 获取iam的登录地址
      getIAMLoginUrl()
        .then((res) => {
          const loginUrl = new URL(res.data)
          // 将 SSO 登录后的回跳地址统一改到当前站点：
          // 否则 redirect_uri/state 固定为后端配置的远程域名（如 eiip.hdt.cosmoplat.com），
          // 本地联调时登录后会被带回远程站点，token cookie 也无法落在 localhost。
          // 生产环境 origin 与远程域名一致，行为不变。
          loginUrl.searchParams.set(
            'redirect_uri',
            `${window.location.origin}/base/user/loginCallBack`
          )
          loginUrl.searchParams.set('state', window.location.href)
          // 登录之前的地址保存
          localStorage.setItem('beforeUrl', window.location.href)
          // 跳转到对应的登录地址
          window.location.href = loginUrl.toString()
          resolve()
        })
        .catch((err) => {
          reject(err)
        })
    })
  },

  validateToken({ commit, state, dispatch }, token) {
    return new Promise((resolve, reject) => {
      validateToken({
        // token
        accessToken: token
      })
        .then((response) => {
          console.log(response, 'response')
          const { code } = response
          if (code != 200) {
            return reject('token无效')
          }
          resolve(code)
        })
        .catch((error) => {
          console.log(error, 'specialError')
          reject(error)
        })
    })
  },
  isAdmin({ commit, state, dispatch }) {
    return new Promise((resolve, reject) => {
      isAdmin()
        .then((response) => {
          const { data } = response
          commit('SET_ISADMIN', data)
          resolve(data)
        })
        .catch((error) => {
          console.log(error, 'specialError')
          reject(error)
        })
    })
  },

  /**
   * 获取用户信息
   * @param {*} param0
   * @returns {Promise}
   */
  getInfo({ commit, state, dispatch }) {
    return new Promise((resolve, reject) => {
      getInfo({
        // token
        access_token: getToken(),
        t: new Date().getTime()
      })
        .then((response) => {
          const { data } = response
          if (!data) {
            return reject('获取用户信息失败')
          }
          // 用户信息
          const _userInfo = data
          // 切换账号检测：agent token 与工作区是按账号隔离的，若当前登录用户
          // 与上一次记录的 agent token 归属用户不一致，必须清除旧账号残留的
          // agent token / 工作区 / 智能体缓存，否则 fetchAgentToken 会复用旧
          // token 拉到上一个账号的会话记录（普通浏览器"会话记录乱"的根因）。
          try {
            const currentOwner = _userInfo && _userInfo.userCode
            if (currentOwner) {
              const lastOwner = localStorage.getItem(AGENT_TOKEN_OWNER_KEY)
              if (lastOwner && lastOwner !== String(currentOwner)) {
                clearAgentToken()
              }
              localStorage.setItem(AGENT_TOKEN_OWNER_KEY, String(currentOwner))
            }
          } catch (e) {
            // ignore: 存储不可用时不阻塞登录主流程
          }
          // 获取ihaier用户信息
          // dispatch('getIhaierUserInfo', _userInfo.userCode)
          // 设置用户信息
          commit('SET_USERINFO', _userInfo)
          // 设置用户名
          commit('SET_NAME', _userInfo.userName)
          commit('SET_AVATAR', _userInfo.avatar)
          resolve(data)
        })
        .catch((error) => {
          console.log(error, 'specialError')
          reject(error)
        })
    })
  },
  /**
   * 获取用户信息
   * @param {*} param0
   * @returns {Promise}
   */
  getTenantList({ commit, state, dispatch }) {
    if (!(state.sysConfig.showTenant === true)) return
    return new Promise((resolve, reject) => {
      getTenantList({})
        .then((response) => {
          const { data } = response
          if (!data) {
            return reject('获取租户信息失败')
          }
          const _tenantList = data.records || []
          let _currentTenant = _tenantList.filter(
            (item) => item.isCurrentTenant === true
          )
          if (_currentTenant.length === 0 && _tenantList.length > 0) {
            _currentTenant = _tenantList[0]
          }
          commit('SET_TENANT_LIST', _tenantList)
          commit('SET_CURRENT_TENANT', _currentTenant[0])
          resolve(data)
        })
        .catch((error) => {
          console.log(error, 'specialError')
          resolve(null)
        })
    })
  },
  // 切换租户
  changeTenant({ commit, state, dispatch }, tenantId) {
    return new Promise((resolve, reject) => {
      changeCurrentTenant(tenantId)
        .then((response) => {
          const { code } = response
          if (code != 200) {
            return reject('切换租户失败')
          } else {
            window.location.reload()
          }
          resolve(code)
        })
        .catch((error) => {
          console.log(error, 'specialError')
        })
      // .finally(() => {
      //   // 刷新页面
      //   window.location.reload()
      // })
    })
  },

  /**
   * 获取ihaier用户信息
   * @returns {void}
   */
  getIhaierUserInfo({ commit }, userCode) {
    // 请求ihaier用户信息
    requestIHaierUserInfo(userCode).then(({ data }) => {
      // 用户数据
      const userData = data.data
      // 设置头像
      commit('SET_AVATAR', userData.avatar240)
    })
  },
  handleIFrameLogout({ commit }, frontLogoutUrl) {
    if (Array.isArray(frontLogoutUrl) && frontLogoutUrl.length > 0) {
      const container = document.createElement('div')
      container.style.display = 'none' // 隐藏容器

      const iframePromises = []

      frontLogoutUrl.forEach((url) => {
        const iframe = document.createElement('iframe')
        iframe.src = url + '?logoutUserToken=' + getToken()
        const loadPromise = new Promise((resolve, reject) => {
          iframe.onload = () => resolve()
          iframe.onerror = () =>
            reject(new Error(`Failed to load iframe: ${url}`))
        })
        iframePromises.push(loadPromise)
        container.appendChild(iframe)
      })

      document.body.appendChild(container)
      // 等待所有 iframe 加载完成后进行跳转
      Promise.all(iframePromises)
        .then(() => {
          let pageUrl = window.location.href
          if (pageUrl.includes('/noPermission')) {
            pageUrl = window.location.origin
          }
          logout({
            pageUrl: pageUrl
          }).then((res) => {
            Cookies.remove('congress')
            removeToken()
            // 清除 tianzhi agent token
            clearAgentToken()
            // 重置状态
            store.commit('user/RESET_STATE')
            store.commit('app/CLEAR_OPENED_APPIDS')
            sessionStorage.clear()
            // 调用iam退出登录
            // const logoutUrl = `https://iam-test.haier.net/?client_id=K5a18f12dc45c2f9d&response_type=code&state=${window.location.href}&redirect_uri=http%3A%2F%2F10.206.65.161%3A33853%2Fuser%2FloginCallBack#exit`
            const logoutUrl = res.data
            window.location.href = encodeURI(logoutUrl)
          })
        })
        .catch((error) => {
          console.error('Iframe加载失败:', error)
          let pageUrl = window.location.href
          if (pageUrl.includes('/noPermission')) {
            pageUrl = window.location.origin
          }
          logout({
            pageUrl: pageUrl
          }).then((res) => {
            Cookies.remove('congress')
            removeToken()
            // 清除 tianzhi agent token
            clearAgentToken()
            // 重置状态
            store.commit('user/RESET_STATE')
            store.commit('app/CLEAR_OPENED_APPIDS')
            sessionStorage.clear()
            // 调用iam退出登录
            // const logoutUrl = `https://iam-test.haier.net/?client_id=K5a18f12dc45c2f9d&response_type=code&state=${window.location.href}&redirect_uri=http%3A%2F%2F10.206.65.161%3A33853%2Fuser%2FloginCallBack#exit`
            const logoutUrl = res.data
            window.location.href = encodeURI(logoutUrl)
          })
        })
    } else {
      let pageUrl = window.location.href
      if (pageUrl.includes('/noPermission')) {
        pageUrl = window.location.origin
      }
      logout({
        pageUrl: pageUrl
      }).then((res) => {
        Cookies.remove('congress')
        removeToken()
        // 清除 tianzhi agent token
        clearAgentToken()
        // 重置状态
        store.commit('user/RESET_STATE')
        store.commit('app/CLEAR_OPENED_APPIDS')
        sessionStorage.clear()
        // 调用iam退出登录
        // const logoutUrl = `https://iam-test.haier.net/?client_id=K5a18f12dc45c2f9d&response_type=code&state=${window.location.href}&redirect_uri=http%3A%2F%2F10.206.65.161%3A33853%2Fuser%2FloginCallBack#exit`
        const logoutUrl = res.data
        window.location.href = encodeURI(logoutUrl)
      })
    }
  },

  /**
   * 退出登录
   * @param {*} param0
   * @returns {Promise}
   */
  logout({ commit, state, dispatch }) {
    return new Promise((resolve, reject) => {
      if (
        store.state.app.openedAppIds &&
        store.state.app.openedAppIds.length > 0
      ) {
        const appIds = store.state.app.openedAppIds.join(',')
        requestApps(appIds).then((res) => {
          const openAppUrls = []
          if (Array.isArray(res.data)) {
            res.data.forEach((app) => {
              if (app.logoutUrl) {
                openAppUrls.push(app.logoutUrl)
              }
            })
          }
          dispatch('handleIFrameLogout', openAppUrls)
        }).catch((error) => {
          // 查询子应用登出地址失败（如openedAppIds含后端查不到的应用id）不能阻塞登出主流程
          console.error('查询子应用登出地址失败，继续执行登出:', error)
          dispatch('handleIFrameLogout', [])
        })
      } else {
        dispatch('handleIFrameLogout', [])
      }
    })
  },

  /**
   * 重置token
   * @param {*} param0
   * @returns {Promise}
   */
  async resetToken({ commit, dispatch }) {
    function performLogout(commit) {
      // 移除令牌
      removeToken()
      // 清除 tianzhi agent token（避免强制登出后被下一个账号复用）
      clearAgentToken()
      // 重置状态
      commit('RESET_STATE')
      sessionStorage.clear()
      console.log('刷新token异常，登出！')
      window.location.reload()
    }
    try {
      // 从 Cookies 中获取刷新令牌
      const refreshToken = Cookies.get(
        store.state.user.sysConfig.refreshTokenCookieName
      )
      // 检查刷新令牌是否存在或为空
      if (!refreshToken || refreshToken.trim() === '') {
        // 若刷新令牌无效，执行登出操作
        performLogout(commit)
        return
      }

      // 调用刷新令牌的 API
      const res = await refreshTokenApi(refreshToken)
      // 若状态码为 200，更新令牌
      commit('SET_TOKEN', res.data)
    } catch (error) {
      // 处理刷新令牌过程中出现的错误，执行登出操作并抛出异常
      performLogout(commit)
      throw error
    }
  },
  /**
   * 获取用户权限菜单
   * @param {*} param0
   * @returns {Promise}
   */
  getUserMenus({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      appWithCanAccess().then(({ data }) => {
        const newData = [
          {
            appList: [...data]
          }
        ]
        store.state.app.dataList = newData
        sessionStorage.setItem('dataList', JSON.stringify(newData))
        const appList = []
        const appListData = data
        appList.push(
          ...data.map((app) => ({
            apiUrl: app.apiUrl,
            url: app.url,
            context: app.context,
            owner: app.ownerUserCode,
            canAccess: app.canAccess,
            isOwner: app.ownerUserCode === store.state.user.userInfo.userCode
          }))
        )
        resolve(appListData)
        sessionStorage.setItem('appList', JSON.stringify(appList))
        commit('SET_APP_LIST', appListData)
      })
    })
  }
}

// 记录当前 agent token 所属的用户标识（userCode），用于切换账号时检测身份变化
const AGENT_TOKEN_OWNER_KEY = 'claw_agent_token_owner'

/**
 * 清除当前环境的 tianzhi agent token 及其关联的智能体缓存。
 * 切换账号/退出登录时必须调用，避免旧账号的 token / 工作区 / 智能体列表
 * 被下一个账号复用，导致会话记录回显串号。
 */
function clearAgentToken() {
  const key = process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
  // 1) 删前端写入的那条：缺省 domain（当前精确子域）+ path=/
  Cookies.remove(key, { path: '/' })
  // 2) 删天智服务端跨子域 Set-Cookie 写入的那条：它落在"去掉当前 hostname 首段"的共享域上
  //    （如 eiip.hdt.cosmoplat.com -> .hdt.cosmoplat.com；eiip.cosmoplat.com -> .cosmoplat.com）。
  //    js-cookie 删除必须 domain 完全匹配才生效，故这里从"去掉首段"起逐级父域各删一遍，
  //    直到末两段根域为止，覆盖各环境（含可能残留在更上层 .cosmoplat.com 的老 cookie），删多余 domain 无副作用。
  try {
    const parts = (window.location.hostname || '').split('.')
    for (let i = 1; i <= parts.length - 2; i++) {
      const domain = '.' + parts.slice(i).join('.')
      Cookies.remove(key, { path: '/', domain })
    }
  } catch (e) {
    // ignore: hostname 异常时忽略，至少已删掉精确子域那条
  }
  // 清除会话级内存缓存，否则 fetchAgentToken 仍会复用上一个账号的 token
  _lastAgentToken = ''
  _agentTokenPromise = null
  // 清除 agent 归属身份与关联的本地缓存（工作区 / 用户名 / 智能体列表）
  try {
    localStorage.removeItem(AGENT_TOKEN_OWNER_KEY)
    localStorage.removeItem('claw_workspace_id')
    localStorage.removeItem('cosmo_claw_user_name')
    localStorage.removeItem(AGENT_LIST_CACHE_KEY)
  } catch (e) {
    // ignore: 存储不可用时忽略
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
